import { useEffect, useState } from "react";
import { marketingApi, getToken, setToken } from "@/lib/marketing";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Field, TextInput } from "./shared";
import CreateLinkForm from "./CreateLinkForm";
import LinksLibrary from "./LinksLibrary";
import BulkTab from "./BulkTab";
import SettingsTab from "./SettingsTab";
import PerformancePanel from "./PerformancePanel";

export default function AdminMarketing() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("links");
  const [refreshToken, setRefreshToken] = useState(0);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [loginErr, setLoginErr] = useState("");

  useEffect(() => {
    (async () => {
      if (getToken()) {
        try {
          const u = await marketingApi.me();
          setUser(u);
        } catch {
          setToken(null);
        }
      }
      setChecking(false);
    })();
  }, []);

  const bump = () => setRefreshToken((t) => t + 1);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    try {
      const r = await marketingApi.login(email.trim(), pw);
      setToken(r.access_token);
      setUser(r.user);
    } catch (err) {
      setLoginErr(err.message || "Login failed");
    }
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center font-body text-ink-soft">Loading…</div>;
  }

  if (!user) {
    return (
      <section className="min-h-screen flex items-center justify-center px-5 py-24" data-testid="marketing-admin-login">
        <div className="w-full max-w-md bg-paper border border-ink/12 p-8 md:p-10 shadow-[4px_8px_28px_rgba(27,27,24,0.08)]">
          <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Tracking Links & Attribution</p>
          <h1 className="font-serif text-3xl leading-tight text-ink">Sign in to manage links.</h1>
          <form onSubmit={handleLogin} className="mt-8 space-y-4" data-testid="marketing-login-form">
            <Field label="Email"><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="marketing-login-email" /></Field>
            <Field label="Password"><TextInput type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="marketing-login-password" /></Field>
            {loginErr && <p className="font-body text-sm text-terracotta" data-testid="marketing-login-error">{loginErr}</p>}
            <button type="submit" data-testid="marketing-login-submit"
              className="w-full bg-ink text-paper py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors">
              Sign in
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-16 md:py-24" data-testid="marketing-admin-panel">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta">Marketing</p>
            <h1 className="font-serif text-3xl md:text-4xl text-ink">Tracking Links & Attribution</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-ink-soft">{user.email}</span>
            <button type="button" onClick={() => { setToken(null); setUser(null); }} data-testid="marketing-logout"
              className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta">
              Sign out
            </button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} data-testid="marketing-tabs">
          <TabsList className="bg-paper-deep">
            <TabsTrigger value="links" data-testid="tab-links">Links</TabsTrigger>
            <TabsTrigger value="bulk" data-testid="tab-bulk">Bulk create</TabsTrigger>
            <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="links" className="mt-6">
            <div className="grid lg:grid-cols-[1.1fr_1.4fr] gap-8">
              <CreateLinkForm onCreated={bump} />
              <LinksLibrary refreshToken={refreshToken} onChanged={bump} />
            </div>
          </TabsContent>
          <TabsContent value="bulk" className="mt-6">
            <BulkTab onCreated={bump} />
          </TabsContent>
          <TabsContent value="performance" className="mt-6">
            <PerformancePanel />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
